import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { DataTable } from 'primereact/datatable';
import { Row } from 'primereact/row';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { emissionValueWithDecimals } from '../../../../../../../../../../../shared/helpers/emission-value-with-decimals';
import { DeclaredUnitToPlainSymbolsMap } from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import {
  EmissionSymbol,
  IProcessDetails,
} from '../../../../../../../../../../../shared/interfaces/IFootprint';
import { EnergySourceUnitMap } from '../../../PcfWizard.type';
import { ENERGY_SOURCE_LABEL_KEYS, EnergyTableRow, getEnergyTableRows } from '../helpers';
import TableHeaderWithUnit from './TableHeaderWithUnit';

type EnergyTableProps = {
  machines?: IProcessDetails['machines'];
  /** Sum of the emissions of all energy sources of the process. */
  totalEmissions?: number;
};

/** The energy sources of an Individual Process, one row each, with their total underneath. */
const EnergyTable = ({ machines, totalEmissions }: EnergyTableProps) => {
  const { t } = useTranslation();

  const rows = getEnergyTableRows(machines);

  if (rows.length === 0) {
    return null;
  }

  const energyTemplate = (row: EnergyTableRow) => {
    return t(ENERGY_SOURCE_LABEL_KEYS[row.source]);
  };

  // The unit depends on the source (kWh or m³), so it stays in the cell.
  const amountTemplate = (row: EnergyTableRow) => {
    return row.amount == null
      ? ''
      : `${emissionValueWithDecimals(row.amount)} ${
          DeclaredUnitToPlainSymbolsMap[EnergySourceUnitMap[row.source]]
        }`;
  };

  // "kg CO₂e" is in the column header, so only the per-unit part is shown next to the value.
  const emissionFactorTemplate = (row: EnergyTableRow) => {
    return (
      <EmissionValueWithSuffix
        emissionValue={row.emissionFactor}
        declaredUnit={EnergySourceUnitMap[row.source]}
        unitaryProductAmount={1}
        abbreviateUnit={true}
        emissionValueDecimals={6}
        numericalValueTextClassName='font-normal'
        suffixTextClassName='font-normal'
      />
    );
  };

  const sourceTemplate = (row: EnergyTableRow) => {
    return <span className='text-color-secondary'>{row.dataSource}</span>;
  };

  const emissionsTemplate = (row: EnergyTableRow) => {
    return row.emissions == null ? '' : emissionValueWithDecimals(row.emissions);
  };

  // The label spans every column but the last, so the total sits under the emissions it sums. The
  // unit is already in the last column's header.
  const footerColumnGroup =
    totalEmissions == null ? undefined : (
      <ColumnGroup>
        <Row>
          <Column
            footer={t('calculationDetailsPage.sections.individualProcess.table.energyTotal')}
            colSpan={4}
            footerClassName='font-semibold'
          />
          <Column
            footer={emissionValueWithDecimals(totalEmissions)}
            footerClassName='font-semibold'
            footerStyle={{ textAlign: 'right' }}
          />
        </Row>
      </ColumnGroup>
    );

  return (
    <DataTable
      value={rows}
      dataKey='key'
      size='small'
      className='text-sm border-round-xl overflow-hidden'
      tableStyle={{ tableLayout: 'fixed', wordBreak: 'break-word' }}
      footerColumnGroup={footerColumnGroup}
    >
      <Column
        header={t('calculationDetailsPage.sections.individualProcess.table.energy')}
        body={energyTemplate}
        style={{ width: '32%' }}
      />
      <Column
        header={t('calculationDetailsPage.sections.individualProcess.table.amount')}
        body={amountTemplate}
        align='right'
        alignHeader='right'
        style={{ width: '12%' }}
      />
      <Column
        header={
          <TableHeaderWithUnit
            label={t('calculationDetailsPage.sections.individualProcess.table.emissionFactor')}
            unit={EmissionSymbol.KG_CO2E}
          />
        }
        body={emissionFactorTemplate}
        style={{ width: '24%' }}
      />
      <Column
        header={t('calculationDetailsPage.sections.individualProcess.table.source')}
        body={sourceTemplate}
        style={{ width: '20%' }}
      />
      <Column
        header={
          <TableHeaderWithUnit
            label={t('calculationDetailsPage.sections.individualProcess.table.emissions')}
            unit={EmissionSymbol.KG_CO2E}
            alignEnd
          />
        }
        body={emissionsTemplate}
        align='right'
        alignHeader='right'
        style={{ width: '12%' }}
      />
    </DataTable>
  );
};

export default EnergyTable;
